import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { DealExitStatusEnumType, DealExitTypeEnumType } from '__generated__/graphql';
import { FC } from 'react';

import Button from 'primitives/Button';

import { FormInput, FormPanelWithReadMode } from 'components/FormPanel';

import dealExitTypeToReadable from 'utils/enums/deal-exit-type-to-readable';

const UPDATE_DEAL_EXIT_MUTATION = gql(`
  mutation UpdateDealExit(
    $id: ID!,
    $type: DealExitTypeEnumType,
    $percentageOfShares: Float,
    $pricePerShare: Float,
    $exitedAt: String,
    $comments: String,
    $footnotes: String,
    $distributionExpenses: Float,
    $multipleOnInvestment: Float,
    $totalSaleProceeds: Float,
    $applyCostIndexation: Boolean,
    $distributionStatementFootnotes: [String!],
    $customDistributionStatementFootnotes: String
  ) {
    updateDealExit(
      id: $id,
      type: $type,
      percentageOfShares: $percentageOfShares,
      pricePerShare: $pricePerShare,
      exitedAt: $exitedAt,
      comments: $comments,
      footnotes: $footnotes,
      distributionExpenses: $distributionExpenses,
      multipleOnInvestment: $multipleOnInvestment,
      totalSaleProceeds: $totalSaleProceeds,
      applyCostIndexation: $applyCostIndexation,
      distributionStatementFootnotes: $distributionStatementFootnotes,
      customDistributionStatementFootnotes: $customDistributionStatementFootnotes
    ) {
      id
      type
      percentageOfShares
      pricePerShare
      status
      exitedAt
      createdAt
      comments
      footnotes
      distributionExpenses
      multipleOnInvestment
      totalSaleProceeds
      applyCostIndexation
      distributionStatementFootnotes {
        selectedFootnotes
        customFootnotes
      }
    }
  }
`);

interface UpdateDealExitProps {
  dealExit: {
    id: string;
    type: string;
    percentageOfShares: number;
    pricePerShare: number;
    exitedAt: string;
    status: string;
    comments?: string | null;
    footnotes?: string | null;
    distributionExpenses?: number | null;
    multipleOnInvestment: number;
    totalSaleProceeds?: number | null;
    applyCostIndexation?: boolean | null;
    distributionStatementFootnotes?: {
      selectedFootnotes: string[];
      customFootnotes: string;
    } | null;
  };
  deal: {
    id: string;
    schemeName?: string | null;
    companyInformation: {
      company: {
        name: string;
      };
    };
  };
}

const UpdateDealExit: FC<UpdateDealExitProps> = ({ dealExit, deal }) => {
  const [updateDealExit, { loading, error }] = useMutation(UPDATE_DEAL_EXIT_MUTATION);
  const handleSubmit = async (data: any) => {
    await updateDealExit({
      variables: {
        id: dealExit.id,
        type: data.type,
        percentageOfShares: parseFloat(data.percentageOfShares),
        pricePerShare: parseFloat(data.pricePerShare),
        exitedAt: data.exitedAt,
        comments: data.comments,
        footnotes: data.footnotes,
        distributionExpenses: data.distributionExpenses
          ? parseFloat(data.distributionExpenses)
          : null,
        multipleOnInvestment: parseFloat(data.multipleOnInvestment),
        totalSaleProceeds: data.totalSaleProceeds ? parseFloat(data.totalSaleProceeds) : null,
        applyCostIndexation: data.applyCostIndexation,
        distributionStatementFootnotes: data.distributionStatementFootnotes || [],
        customDistributionStatementFootnotes: data.customDistributionStatementFootnotes || '',
      },
    });
  };

  return (
    <FormPanelWithReadMode onSubmit={handleSubmit} loading={loading} error={error}>
      <Button variant="secondary" onClick={() => window.open(`/deals/${deal.id}`, '_blank')}>
        {deal.schemeName}
      </Button>
      <FormInput
        type="text"
        fieldName="company"
        label="Company"
        defaultValue={deal.companyInformation.company.name}
        editable={false}
      />
      <FormInput
        type="select"
        fieldName="type"
        label="Type"
        defaultValue={dealExit.type}
        options={Object.values(DealExitTypeEnumType).map(type => ({
          label: dealExitTypeToReadable(type).label,
          value: type,
        }))}
      />
      <FormInput
        type="number"
        fieldName="percentageOfShares"
        label="Percentage of Shares"
        defaultValue={dealExit.percentageOfShares}
        validators={{ required: true }}
      />
      <FormInput
        type="number"
        fieldName="pricePerShare"
        label="Price Per Share"
        helperText="Please enter the exact price per share, including all decimal places"
        defaultValue={dealExit.pricePerShare}
        validators={{ required: true }}
      />
      <FormInput
        type="date"
        fieldName="exitedAt"
        label="Exited At"
        defaultValue={dealExit.exitedAt}
        validators={{ required: true }}
      />
      <FormInput
        type="select"
        fieldName="status"
        label="Status"
        defaultValue={dealExit.status}
        options={[
          { label: 'Initiated', value: DealExitStatusEnumType.Initiated },
          { label: 'Finalised', value: DealExitStatusEnumType.Finalised },
        ]}
      />
      <FormInput
        type="text"
        fieldName="comments"
        label="Comments"
        defaultValue={dealExit.comments}
      />
      <FormInput
        type="text"
        fieldName="footnotes"
        label="Footnotes"
        defaultValue={dealExit.footnotes}
      />
      <FormInput
        type="number"
        fieldName="distributionExpenses"
        label="Distribution Expenses"
        defaultValue={dealExit.distributionExpenses}
      />
      <FormInput
        type="number"
        fieldName="multipleOnInvestment"
        label="Multiple on Investment"
        defaultValue={dealExit.multipleOnInvestment}
        validators={{ required: true }}
      />
      <FormInput
        type="currency"
        fieldName="totalSaleProceeds"
        label="Total Sale Proceeds"
        helperText="Total amount received from the sale"
        defaultValue={dealExit.totalSaleProceeds}
      />
      <FormInput
        type="switch"
        fieldName="applyCostIndexation"
        label="Apply Cost Indexation"
        defaultValue={dealExit.applyCostIndexation}
      />
      <FormInput
        type="multi_select"
        fieldName="distributionStatementFootnotes"
        label="Distribution Statement Footnotes"
        defaultValue={dealExit.distributionStatementFootnotes?.selectedFootnotes || []} // Populate from prop
        options={[
          {
            label: 'Tax Deducted at Source - 10%',
            value:
              'Tax has been withheld at the rate of 10 percent in case of resident investors and at the rates in force for the non-resident investors, as per the prevailing Indian income-tax provisions.',
          },
          {
            label: 'Distribution(s) may be recalled',
            value:
              'Distribution(s) may be recalled by AngelList India, if necessary, to satisfy any obligations of the Fund including indemnification, tax or otherwise in relation to this scheme.',
          },
          {
            label: 'Buyback Proceeds (Profitable)',
            value:
              'Please note that as per section 2(22)(f) of the Income-tax Act, 1961, proceeds from the buyback will be treated as deemed dividends and taxed accordingly at the applicable rates. Furthermore, investors may claim the cost of acquisition for the bought-back securities as a capital loss in their tax returns.',
          },
          {
            label: 'Buyback Loss',
            value:
              'The cost of acquisition for the securities being bought back shall be claimed as a capital loss in your tax return.',
          },
          {
            label: 'Bonus Shares',
            value: ' The cost of acquisition is nil for the bonus shares.',
          },
        ]}
      />
      <p className="flex items-center col-span-full">
        <a
          href="https://docs.google.com/spreadsheets/d/1henWQAW_9Xjj2xV-3YRgCiyjo28IhGR4/edit?gid=492949198#gid=492949198"
          target="_blank"
          rel="noreferrer"
          className="text-blue-500 hover:text-blue-600"
        >
          Footnotes Reference Sheet
        </a>{' '}
      </p>
      <FormInput
        type="textarea"
        fieldName="customDistributionStatementFootnotes"
        label="Custom Distribution Statement Footnote"
        defaultValue={dealExit.distributionStatementFootnotes?.customFootnotes || ''} // Populate from prop
      />
    </FormPanelWithReadMode>
  );
};

export default UpdateDealExit;
